<script setup>
import {computed, defineProps, defineEmits, ref} from "vue";
import {clickOutside} from "@/composables/directives";

// Регистрация директивы v-click-outside
const vClickOutside = clickOutside;

const props = defineProps({
  /**
   * Год, например: 2025
   */
  year: {
    type: Number,
    required: true,
  },
  /**
   * Месяц в диапазоне 0–11 (0 — январь, 11 — декабрь)
   */
  month: {
    type: Number,
    required: true,
  },
  /** Файл локализации */
  localeMessages: {
    type: Object,
    required: true
  },
  /** До какого дня разрешено выбирать дату */
  maxDate: {
    type: [Date, null],
    default: null
  }
});

const emit = defineEmits(["switch-to-next-month", "switch-to-previous-month", "switch-month", "switch-year"]);

const monthName = computed(() => props.localeMessages.months.long[props.month]);

// Является ли активный месяц последним разрешенным.
const isLastMonth = computed(() => {

  if (props.maxDate) {
    const y = props.year;
    const m = props.month;

    const maxYear = props.maxDate.getFullYear();
    const maxMonth = props.maxDate.getMonth();

    return y === maxYear && m >= maxMonth;
  } else {
    return false;
  }
});

// Эмиты переключение по месяцам/годам. Для переключения вперед с проверкой максимальной даты.
const handleSwitchToNextMonth = () => {
  if (isLastMonth.value) {
    return false;
  } else {
    emit("switch-to-next-month");
  }
};

const handleSwitchToPreviousMonth = () => emit("switch-to-previous-month");

// dropdown по месяцам и годам

const isShowMonthDropdown = ref(false);
const hideMonthDropdown = () => isShowMonthDropdown.value = false;
const toggleMonthDropdown = () => isShowMonthDropdown.value = !isShowMonthDropdown.value;
const switchMonth = (monthIndex) => {
  hideMonthDropdown();
  emit("switch-month", monthIndex);
};

const isShowYearDropdown = ref(false);
const hideYearDropdown = () => isShowYearDropdown.value = false;
const toggleYearDropdown = () => isShowYearDropdown.value = !isShowYearDropdown.value;
const switchYear = (year) => {
  hideYearDropdown();
  emit("switch-year", year);
};

const yearsList = computed(() => {
  const currentYear = new Date().getFullYear();
  const start = currentYear - 14;
  return Array.from({ length: 15 }, (_, i) => start + i).reverse();
});
</script>

<template>
  <div class="month-year-selector">
    <div
      class="month-year-selector__previous month-year-selector__button"
      @click="handleSwitchToPreviousMonth"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15L7 10L12 5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <div
      v-click-outside="hideMonthDropdown"
      class="month-year-selector__current-value"
      :class="{'month-year-selector__current-value--active': isShowMonthDropdown}"
      @click.self="toggleMonthDropdown"
    >
      {{ monthName }}
      <div
        v-if="isShowMonthDropdown"
        class="month-year-selector__wrapper"
      >
        <ul class="month-year-selector__dropdown scroll">
          <li
            v-for="(value, monthIndex) in localeMessages.months.long"
            :key="value"
            class="month-year-selector__dropdown-item"
            :class="{'month-year-selector__dropdown-item--active': props.month === monthIndex}"
            @click="switchMonth(monthIndex)"
          >
            {{ value }}
          </li>
        </ul>
      </div>
    </div>

    <div
      v-click-outside="hideYearDropdown"
      class="month-year-selector__current-value"
      :class="{'month-year-selector__current-value--active': isShowYearDropdown}"
      @click.self="toggleYearDropdown"
    >
      {{ year }}
      <div
        v-if="isShowYearDropdown"
        class="month-year-selector__wrapper"
      >
        <ul class="month-year-selector__dropdown scroll">
          <li
            v-for="yearItem in yearsList"
            :key="yearItem"
            class="month-year-selector__dropdown-item"
            :class="{'month-year-selector__dropdown-item--active': props.year === yearItem}"
            @click="switchYear(yearItem)"
          >
            {{ yearItem }}
          </li>
        </ul>
      </div>
    </div>

    <div
      class="month-year-selector__next month-year-selector__button"
      :class="{'month-year-selector__next--last': isLastMonth}"
      @click="handleSwitchToNextMonth"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 15L13 10L8 5"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </div>
</template>

<style scoped lang="scss">

ul, li {
  all: unset;
}

.month-year-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__wrapper {
    top: calc(100% + 10px);
    left: 0;
    box-shadow: 1px 2px 10px 0 #131E2726;
    border-radius: 8px;
    padding: 6px 3px 6px 6px;
    background: #FFFFFF;
    position: absolute;

    z-index: 9999;
  }

  &__dropdown {
    display: flex;
    flex-direction: column;
    position: relative;
    max-height: 234px;
    padding-right: 3px;

    gap: 5px;

    &-item {
      padding: 6px 14px;
      border-radius: 8px;
      cursor: pointer;
      user-select: none;
      color: #373D3F;
      font-size: 14px;
      font-weight: 400;

      &:hover {
        background: #EEF1F4;
      }

      &--active {
        color: #1F9AFF;

        &:hover {
          background: #E0F0FF;
        }
      }
    }
  }

  &__current-value {
    padding: 2px 8px;
    font-size: 14px;
    font-weight: 400;
    color: #898989;
    line-height: 18px;
    position: relative;
    cursor: pointer;
    user-select: none;
    border-radius: 8px;

    &:hover {
      background: #E0F0FF;
    }

    &--active {
      background: #E0F0FF;
      color: #1F9AFF;
    }
  }


  &__button {
    padding: 2px;
    cursor: pointer;
    user-select: none;
    align-self: center;
    line-height: 0;
    color: #7B87A5;

    &:hover {
      color: #1F9AFF;
      background: #E0F0FF;
      border-radius: 20px;
    }
  }

  &__next {
    &--last {
      color: #DEDEDE;

      pointer-events: none;
      &:hover {
        color: #DEDEDE;
        background: none;
      }
    }
  }

  .month-year-selector__button,
  .month-year-selector__current-value {
    box-sizing: border-box;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// Стили для скрола
.scroll {
  overflow-y: auto;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: #1A64D433 transparent;
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}

</style>


